import { Suspense } from 'react';

import {
	mapContentItemToArticle,
	TeamStories as TeamStoriesData,
} from 'services/freeform';

import CardFixture from 'core/generic/components/cards/card-fixture';
import CardNews from 'core/generic/components/cards/card-news';

import { ShowMoreSection } from '@home/components/shared/show-more-section/show-more-section';
import { mapFixtureToCardProps } from '@home/utils/map-fixture-to-card-props';
import { NEWS_IMAGE_FALLBACK } from '@home/utils/news-image-fallback';

import styles from './team-stories.module.scss';

type TeamStoriesProps = {
	data: TeamStoriesData;
};

export const TeamStories = ({ data }: TeamStoriesProps) => {
	const stories = (data.stories ?? []).map(mapContentItemToArticle);
	const lead = stories[0];
	const secondary = stories.slice(1, 3);
	const headingId = `team-stories-${data.heading
		.toLowerCase()
		.replace(/\s+/g, '-')}`;
	const lastResult = data.lastResult ?? data.lastFixture;
	const hasFixtures = Boolean(data.nextFixture || lastResult);
	const hasAd = Boolean(data.advertId) && !hasFixtures;
	const hasAside = hasFixtures || hasAd;

	return (
		<section className={styles.root} aria-labelledby={headingId}>
			<div className={`container ${styles.inner}`}>
				<h2
					id={headingId}
					className={`display-large bold ${styles.label}`}
				>
					{data.heading}
				</h2>

				<div
					className={`${styles.body} ${
						hasAside ? styles['body--withAside'] : ''
					}`}
				>
					<div className={styles.main}>
						<ul className={styles.list}>
							{lead ? (
								<li
									key={`${lead.url}-0`}
									className={`${styles.listItem} ${styles['listItem--large']}`}
								>
									<Suspense
										fallback={
											<a href={lead.url}>{lead.title}</a>
										}
									>
										<CardNews
											fallback={NEWS_IMAGE_FALLBACK}
											data={lead}
											size="Medium"
											type="Icon"
											direction="Column"
											fullWidth
											showVideoDuration={false}
										/>
									</Suspense>
								</li>
							) : null}

							{secondary.map((article, index) => (
								<li
									key={`${article.url}-${index + 1}`}
									className={`${styles.listItem} ${styles['listItem--small']}`}
								>
									<Suspense
										fallback={
											<a href={article.url}>
												{article.title}
											</a>
										}
									>
										<CardNews
											fallback={NEWS_IMAGE_FALLBACK}
											data={article}
											size="Medium"
											type="Icon"
											direction="Column"
											fullWidth
											showVideoDuration={false}
										/>
									</Suspense>
								</li>
							))}
						</ul>

						{data.moreLink ? (
							<ShowMoreSection
								link={data.moreLink}
								label={data.moreLabel || 'More news'}
							/>
						) : null}

						<noscript>
							<ul aria-label={`${data.heading} stories`}>
								{stories.map((story, index) => (
									<li key={`${story.url}-${index}`}>
										<a href={story.url}>{story.title}</a>
									</li>
								))}
							</ul>
						</noscript>
					</div>

					{hasFixtures ? (
						<aside
							className={styles.aside}
							aria-label="Fixtures and results"
						>
							<div className={styles.asideCards}>
								{data.nextFixture ? (
									<Suspense fallback={null}>
										<CardFixture
											{...mapFixtureToCardProps(
												data.nextFixture
											)}
										/>
									</Suspense>
								) : null}
								{lastResult ? (
									<Suspense fallback={null}>
										<CardFixture
											{...mapFixtureToCardProps(
												lastResult
											)}
										/>
									</Suspense>
								) : null}
							</div>
						</aside>
					) : hasAd ? (
						<aside
							className={`${styles.aside} ${styles.asideDesktopOnly}`}
							data-ad-slot={data.advertId}
							aria-label="Side banner"
						>
							<div className={styles.adSlot} aria-hidden="true" />
						</aside>
					) : null}
				</div>
			</div>
		</section>
	);
};

export default TeamStories;
